// @ts-check
/**
 * Shared formatters (I18N-02). Every user-visible date, count, duration and
 * plural goes through here so wording and locale handling stay consistent.
 */

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const steps = /** @type {const} */ ([
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.35],
  ["month", 12],
  ["year", Number.POSITIVE_INFINITY],
]);

/** "2 minutes ago", "yesterday", "in 3 days". @param {number} timestamp @param {number} [now] */
export function formatRelative(timestamp, now = Date.now()) {
  let value = (timestamp - now) / 1000;
  if (Math.abs(value) < 45) return "just now";
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return relative.format(Math.round(value), unit);
    value /= size;
  }
  return relative.format(Math.round(value), "year");
}

/** Locale date such as "Oct 9" or "Oct 9, 2025" outside the current year. @param {number} timestamp */
export function formatDate(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
  });
}

/** Locale date and time. @param {number} timestamp */
export function formatDateTime(timestamp) {
  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

const rules = new Intl.PluralRules();
/**
 * Count with the right noun: plural(1, "item") → "1 item", plural(3, "item") → "3 items".
 * @param {number} count
 * @param {string} one
 * @param {string} [other]
 */
export function plural(count, one, other = `${one}s`) {
  return `${new Intl.NumberFormat().format(count)} ${rules.select(count) === "one" ? one : other}`;
}

/** Bytes as "42 GB" (decimal units). @param {number | null | undefined} bytes */
export function formatBytes(bytes) {
  if (bytes === null || bytes === undefined) return "Not reported";
  const units = ["bytes", "KB", "MB", "GB", "TB"];
  let value = bytes,
    unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: unit ? 1 : 0 }).format(value)} ${units[unit]}`;
}

/** "45s", "3m 20s", "1h 5m". @param {number} milliseconds */
export function formatDuration(milliseconds) {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return `${minutes}m${seconds % 60 ? ` ${seconds % 60}s` : ""}`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;
}

/** Human label for an enum value with a known vocabulary; never shows raw snake_case. @param {Record<string, string>} labels @param {string} value */
export function enumLabel(labels, value) {
  if (Object.hasOwn(labels, value)) return labels[value];
  const words = String(value).replaceAll("_", " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
}
